/**
 * 草稿箱批量发布：每条草稿走与发品页相同的规则/属性/SKU 校验，再提交 publishOrEdit。
 * 平台预检约 40/s；发品提交安全上限 35/s（同店）。草稿箱批量仍串行提交以对齐同店 FIFO。
 */

import { sheinPlatformAPI } from '@/utils/api';
import { parseAttributeTemplate, resolveSkcColorAgainstTemplate } from '@/shein-platform/utils/sheinAttributeTemplate';
import { buildSheinPublishPayload, samplePublishPayloadCurrency } from '@/shein-platform/utils/buildSheinPublishPayload';
import {
  extractDefaultLanguage,
  resolveSkuPackageCapabilities,
} from '@/shein-platform/utils/sheinPublishFillInStandard';
import { resolveShopPublishCurrencies } from '@/shein-platform/utils/resolveSheinPublishCurrency';
import { sheinShopCapabilities } from '@/shein-platform/utils/sheinShopCapabilities';
import { runSheinPublishPreflight } from '@/shein-platform/utils/sheinPublishPreflight';
import {
  extractSheinPublishFailMessage,
  isSheinPublishSuccess,
} from '@/shein-platform/utils/sheinPublishResult';
import { pruneSizeChartForSubmit, skuSizeKey } from '@/shein-platform/utils/sheinSizeChart';
import { validateSheinPublishForm } from '@/shein-platform/utils/validateSheinPublishForm';
import { alignAttributesToCategoryTemplate } from '@/shein-platform/utils/sheinProductAttributeTemplate';
import {
  applyPrintNamesToSheinForm,
  enrichSheinFormPrintNames,
  filterSkcsReadyForPlatformPublish,
  LISTING_NO_PUBLISHABLE_COLOR_MESSAGE,
  printNamesFromSheinPrintImages,
} from '@/pages/ipe/workbench/utils/applyListingItemToSheinPublishForm';
import {
  EMPTY_SHEIN_PRINT_INFRINGEMENT,
  normalizeSheinPrintInfringement,
} from '@/shein-platform/utils/sheinPublishPrintInfringement';

/** 预检等读接口并发上限（平台约 40/s） */
export const SHEIN_BATCH_PUBLISH_CONCURRENCY = 40;

/** 同店发品提交安全上限：平台约 40/s，预留余量用 35 */
export const SHEIN_PUBLISH_SUBMIT_MAX_PER_SECOND = 35;

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * 滑动窗口限流：同一店铺每秒最多 acquire maxPerSecond 次。
 * @param {number} maxPerSecond
 */
export function createPerSecondRateLimiter(maxPerSecond = SHEIN_PUBLISH_SUBMIT_MAX_PER_SECOND) {
  const limit = Math.max(1, Number(maxPerSecond) || SHEIN_PUBLISH_SUBMIT_MAX_PER_SECOND);
  const stamps = [];
  return {
    async acquire() {
      while (true) {
        const now = Date.now();
        while (stamps.length && now - stamps[0] >= 1000) stamps.shift();
        if (stamps.length < limit) {
          stamps.push(now);
          return;
        }
        const waitMs = Math.max(1, 1000 - (now - stamps[0]));
        // eslint-disable-next-line no-await-in-loop
        await sleep(waitMs);
      }
    },
  };
}

function deriveFormCurrency(initial = {}) {
  const top = String(initial.currency || '').trim();
  if (top) return top;
  for (const skc of initial.skcList || []) {
    for (const sku of skc.skuRows || []) {
      const code = String(sku?.currency || '').trim();
      if (code) return code;
    }
  }
  return '';
}

function deriveSelectedSizeIds(skcList = []) {
  const ids = [];
  const seen = new Set();
  (skcList || []).forEach((skc) => {
    (skc.skuRows || []).forEach((sku) => {
      const key = skuSizeKey(sku);
      if (key && !seen.has(key)) {
        seen.add(key);
        ids.push(key);
      }
    });
  });
  return ids;
}

function normalizeProductAttributes(raw = {}) {
  const out = {};
  Object.entries(raw || {}).forEach(([id, value]) => {
    if (value == null || value === '') return;
    if (Array.isArray(value)) {
      const asComposition = value.some((row) => (
        row && typeof row === 'object' && ('percent' in row || 'attributeExtraValue' in row || 'valueId' in row)
      ));
      if (asComposition) {
        out[id] = value
          .map((row) => {
            if (row != null && (typeof row === 'string' || typeof row === 'number')) {
              return { valueId: String(row), percent: '' };
            }
            if (!row || typeof row !== 'object') return null;
            const valueId = row.valueId ?? row.attributeValueId;
            if (valueId == null || valueId === '') return null;
            return {
              valueId: String(valueId),
              percent: String(row.percent ?? row.attributeExtraValue ?? ''),
            };
          })
          .filter(Boolean);
        return;
      }
      out[id] = value
        .map((row) => {
          if (row && typeof row === 'object') return String(row.valueId ?? row.attributeValueId ?? '');
          return String(row ?? '');
        })
        .filter(Boolean);
      return;
    }
    if (typeof value === 'object') {
      const valueId = value.valueId ?? value.attributeValueId;
      const percent = value.percent ?? value.attributeExtraValue;
      if (valueId != null && valueId !== '') {
        if (percent != null && String(percent).trim() !== '') {
          out[id] = [{ valueId: String(valueId), percent: String(percent) }];
        } else {
          out[id] = String(valueId);
        }
      } else if (value.attributeExtraValue != null && String(value.attributeExtraValue).trim() !== '') {
        out[id] = String(value.attributeExtraValue);
      }
      return;
    }
    out[id] = value;
  });
  return out;
}

/** 组包前规范化表单；必须保留站内印花图，供编辑页回填。 */
export function emptyPublishForm(initial = {}) {
  return {
    spuName: initial.spuName || '',
    title: initial.title || '',
    titleLanguage: initial.titleLanguage || 'en',
    description: initial.description || '',
    categoryId: initial.categoryId || '',
    productTypeId: initial.productTypeId || '',
    categoryPathLabel: initial.categoryPathLabel || '',
    supplierCode: initial.supplierCode || '',
    brandCode: initial.brandCode || '',
    originCountry: initial.originCountry || '',
    ipId: initial.ipId || '',
    ipName: initial.ipName || '',
    ipNameCn: initial.ipNameCn || '',
    productAttributes: normalizeProductAttributes(initial.productAttributes || {}),
    skcList: Array.isArray(initial.skcList) ? initial.skcList : [],
    selectedSizeIds: Array.isArray(initial.selectedSizeIds)
      ? initial.selectedSizeIds
      : deriveSelectedSizeIds(initial.skcList),
    sizeChart: initial.sizeChart || {},
    sizeSaleAttributeId: initial.sizeSaleAttributeId || '',
    sizeValueLabels: initial.sizeValueLabels && typeof initial.sizeValueLabels === 'object'
      ? initial.sizeValueLabels
      : {},
    // 站内印花图：任务刊登必须带入编辑快照，不能在组包时丢掉
    printImages: Array.isArray(initial.printImages) ? initial.printImages : [],
    ipePrintImageNames: Array.isArray(initial.ipePrintImageNames)
      ? initial.ipePrintImageNames
      : printNamesFromSheinPrintImages(initial.printImages),
    ipePrintInfringement: normalizeSheinPrintInfringement(
      initial.ipePrintInfringement || EMPTY_SHEIN_PRINT_INFRINGEMENT,
    ),
    sheinVideos: Array.isArray(initial.sheinVideos) ? initial.sheinVideos : [],
    supplyPrice: initial.supplyPrice || '',
    currency: deriveFormCurrency(initial),
    stockQty: initial.stockQty || '',
    weightGrams: initial.weightGrams || '',
    weightUnit: initial.weightUnit || 'g',
    pkgLength: initial.pkgLength || '10',
    pkgWidth: initial.pkgWidth || '10',
    pkgHeight: initial.pkgHeight || '10',
    dimUnit: initial.dimUnit || 'cm',
    warehouseId: initial.warehouseId || '',
    warehouseName: initial.warehouseName || '',
    selectedSites: Array.isArray(initial.selectedSites) ? initial.selectedSites : [],
    mainSite: initial.mainSite || 'shein',
    shelfWay: initial.shelfWay || '1',
    hopeOnSaleDate: initial.hopeOnSaleDate || '',
  };
}

/** 未选仓库时默认第一个商家仓（与发品页 withDefaultWarehouse / TEMU 批量准备对齐） */
export function withDefaultSheinWarehouse(form, warehouses = []) {
  const next = form && typeof form === 'object' ? { ...form } : {};
  const rows = Array.isArray(warehouses) ? warehouses : [];
  if (!rows.length) return next;
  const codes = rows
    .map((item) => String(item?.id || item?.warehouseCode || '').trim())
    .filter(Boolean);
  const current = String(next.warehouseId || '').trim();
  if (current && codes.includes(current)) {
    if (!String(next.warehouseName || '').trim()) {
      const hit = rows.find((item) => String(item.id || item.warehouseCode || '') === current);
      next.warehouseName = hit?.name || hit?.warehouseName || '';
    }
    return next;
  }
  const first = rows[0];
  const id = String(first?.id || first?.warehouseCode || '').trim();
  if (!id) return next;
  next.warehouseId = id;
  next.warehouseName = first?.name || first?.warehouseName || '';
  return next;
}

/**
 * @template T, R
 * @param {T[]} items
 * @param {number} concurrency
 * @param {(item: T, index: number) => Promise<R>} worker
 * @param {(done: number, total: number, result: R, index: number) => void} [onItemDone]
 */
export async function mapWithConcurrency(items, concurrency, worker, onItemDone) {
  const list = Array.isArray(items) ? items : [];
  const results = new Array(list.length);
  let cursor = 0;
  let finished = 0;
  const limit = Math.max(1, Math.min(Number(concurrency) || 1, list.length || 1));

  async function runWorker() {
    while (true) {
      const index = cursor;
      cursor += 1;
      if (index >= list.length) return;
      // eslint-disable-next-line no-await-in-loop
      const result = await worker(list[index], index);
      results[index] = result;
      finished += 1;
      onItemDone?.(finished, list.length, result, index);
    }
  }

  if (!list.length) return results;
  await Promise.all(Array.from({ length: limit }, () => runWorker()));
  return results;
}

async function resolveAttrTemplate(shopId, productTypeId, cache) {
  const key = String(productTypeId);
  if (cache.has(key)) return cache.get(key);
  const pending = sheinPlatformAPI.getAttributeTemplate(shopId, {
    product_type_id_list: [Number(productTypeId)],
  }).then((raw) => parseAttributeTemplate(raw, { productTypeId }));
  cache.set(key, pending);
  try {
    return await pending;
  } catch (ex) {
    cache.delete(key);
    throw ex;
  }
}

function emptyPrepareCaches() {
  return {
    fillInStandard: new Map(),
  };
}

export { emptyPrepareCaches };

function buildSubmitForm(form, { colorAttr, sizeAttr, alignedCurrency }) {
  const sizeValueLabels = { ...(form.sizeValueLabels || {}) };
  (sizeAttr?.values || []).forEach((v) => {
    if (v?.id != null && v?.name) sizeValueLabels[String(v.id)] = String(v.name);
  });
  const resolvedSizeSaleAttrId = form.sizeSaleAttributeId || sizeAttr?.attributeId || '';
  const fromSkcSizes = deriveSelectedSizeIds(form.skcList).map((id) => String(id));
  const resolvedSelectedSizeIds = fromSkcSizes.length
    ? fromSkcSizes
    : (form.selectedSizeIds || []).map((id) => String(id));

  const submitFormBase = {
    ...form,
    sizeValueLabels,
    sizeSaleAttributeId: resolvedSizeSaleAttrId,
    selectedSizeIds: resolvedSelectedSizeIds,
    spuName: '',
    currency: alignedCurrency || form.currency,
    skcList: (form.skcList || []).map((skc) => {
      const resolved = resolveSkcColorAgainstTemplate({
        ...skc,
        colorAttributeId: skc.colorAttributeId || colorAttr?.attributeId || '',
      }, colorAttr);
      return {
      ...resolved,
      colorLabel: resolved.colorIsCustom || resolved.colorExtraValue
        ? (resolved.colorExtraValue || resolved.colorLabel)
        : (colorAttr?.values?.find((v) => String(v.id) === String(resolved.colorValueId))?.name
          || resolved.colorLabel),
      skcName: '',
      skuRows: (skc.skuRows || []).map((sku) => ({
        ...sku,
        sizeAttributeId: sku.sizeAttributeId || sizeAttr?.attributeId || resolvedSizeSaleAttrId || '',
        sizeLabel: sizeAttr?.values?.find((v) => String(v.id) === String(sku.sizeValueId))?.name
          || sku.sizeLabel
          || sizeValueLabels[String(sku.sizeValueId || '')]
          || '',
        currency: alignedCurrency || sku.currency || form.currency,
        skuCode: '',
      })),
    };
    }),
  };
  return submitFormBase;
}

export async function waitGoodsSubmitJob(shopId, jobId, onHint) {
  let latest = await sheinPlatformAPI.getGoodsSubmitJob(shopId, jobId);
  while (latest && !latest.done) {
    onHint?.(latest.message || '排队等待调用平台…');
    // eslint-disable-next-line no-await-in-loop
    await sleep(700);
    // eslint-disable-next-line no-await-in-loop
    latest = await sheinPlatformAPI.getGoodsSubmitJob(shopId, jobId);
  }
  return latest;
}

/**
 * 发品表单：属性模板 → 三步预检 → 本地校验 → 组包（与发品页一致）。
 * @returns {Promise<object>}
 */
function dedupeValidationMessages(messages = []) {
  const seen = new Set();
  const out = [];
  (Array.isArray(messages) ? messages : []).forEach((raw) => {
    const message = String(raw || '').trim();
    if (!message || seen.has(message)) return;
    seen.add(message);
    out.push(message);
  });
  return out;
}

function buildPrepareFailure({
  draftId,
  title,
  stage = 'validate',
  message = '',
  messages = [],
  meta = {},
  field,
  section,
}) {
  const deduped = dedupeValidationMessages([...messages, message]);
  return {
    ok: false,
    draftId,
    title,
    stage,
    message: deduped[0] || '准备刊登失败',
    messages: deduped,
    ...(field ? { field } : {}),
    ...(section ? { section } : {}),
    ...meta,
  };
}

export async function prepareFormPublish({
  shopId,
  form: rawForm,
  titleHint = '',
  draftId = null,
  meta = {},
  warehouses = [],
  attrTplCache,
  shopType = '',
  siteList = null,
  omitIncompleteSkcs = false,
  collectAllErrors = false,
  prepareCaches = null,
}) {
  const caches = prepareCaches || emptyPrepareCaches();
  // 模板里不存在、已被跳过的颜色（不进 payload，但必须在日志里说清，避免「颜色悄悄少了」）
  const skippedListingColors = (Array.isArray(rawForm?.ipeSkippedListingColors)
    ? rawForm.ipeSkippedListingColors
    : []
  ).filter(Boolean);
  const describeSkippedListingColors = () => skippedListingColors
    .map((slot) => {
      const label = String(slot?.colorLabel || '').trim();
      const code = String(slot?.colorCode || '').trim();
      const name = String(slot?.mainName || '').trim();
      const shown = [label ? `「${label}」` : '', code ? `编码「${code}」` : ''].filter(Boolean).join('、');
      return shown || (name ? `主图名「${name}」` : '未命名颜色');
    })
    .filter(Boolean);
  const skippedListingColorMessage = () => {
    const list = describeSkippedListingColors();
    if (!list.length) return '';
    return `已跳过模板中不存在的颜色：${list.join('；')}`;
  };
  const validationMessages = [];
  const pushValidationMessage = (message) => {
    const text = String(message || '').trim();
    if (!text) return;
    validationMessages.push(text);
  };
  const failPrepare = (stage, message, extra = {}) => {
    if (collectAllErrors) {
      pushValidationMessage(message);
      return null;
    }
    // 被跳过的颜色必须和失败原因一起露出，否则用户只看到「颜色未匹配」却不知道哪些被丢了
    const skippedNote = skippedListingColorMessage();
    return buildPrepareFailure({
      draftId,
      title: extra.title,
      stage,
      message,
      messages: skippedNote ? [...validationMessages, skippedNote] : validationMessages,
      meta,
      field: extra.field,
      section: extra.section,
    });
  };

  let form = emptyPublishForm(rawForm || {});
  if (omitIncompleteSkcs) {
    const skcList = filterSkcsReadyForPlatformPublish(form.skcList);
    form = {
      ...form,
      skcList,
      selectedSizeIds: deriveSelectedSizeIds(skcList),
    };
    if (!skcList.length) {
      const titleEarly = String(form.title || titleHint || meta.title || '').trim() || '未命名商品';
      const earlyFail = failPrepare('validate', LISTING_NO_PUBLISHABLE_COLOR_MESSAGE, { title: titleEarly });
      if (earlyFail) return earlyFail;
    }
  }
  form = applyPrintNamesToSheinForm(await enrichSheinFormPrintNames(form));
  if (shopType) form = { ...form, shopType };
  // 自动规则 / 工作流批量准备：与发品页一致，未选手动仓时默认第一个商家仓
  form = withDefaultSheinWarehouse(form, warehouses);
  // 商品库标题优先写入表单；title 变量仅用于进度展示时也必须回写，避免 payload 标题为空
  const resolvedTitle = String(form.title || titleHint || meta.title || '').trim()
    || String(titleHint || '').trim()
    || '未命名商品';
  form = { ...form, title: resolvedTitle };
  const title = resolvedTitle;

  if (!form.categoryId) {
    const earlyFail = failPrepare('validate', '缺少商品类目', { title });
    if (earlyFail) return earlyFail;
  }
  if (!form.productTypeId) {
    const earlyFail = failPrepare('validate', '缺少商品类型，请重新选择末级类目', { title });
    if (earlyFail) return earlyFail;
  }
  if (!collectAllErrors && validationMessages.length) {
    return buildPrepareFailure({
      draftId,
      title,
      stage: 'validate',
      messages: validationMessages,
      meta,
    });
  }
  if (!form.categoryId || !form.productTypeId) {
    return buildPrepareFailure({
      draftId,
      title,
      stage: 'validate',
      messages: validationMessages,
      meta,
    });
  }

  let attrTpl;
  try {
    attrTpl = await resolveAttrTemplate(shopId, form.productTypeId, attrTplCache);
  } catch (ex) {
    return buildPrepareFailure({
      draftId,
      title,
      stage: 'validate',
      message: ex?.message || '类目属性加载失败',
      messages: validationMessages,
      meta,
    });
  }

  form = {
    ...form,
    productAttributes: alignAttributesToCategoryTemplate(form.productAttributes, attrTpl),
  };

  const colorAttr = attrTpl?.skcSaleAttrs?.[0] || null;
  const sizeAttr = attrTpl?.skuSaleAttrs?.[0] || null;
  const sizeMeasureAttrs = attrTpl?.sizeAttrs || [];

  // 店铺类型决定价格口径：半托管/全托管写 cost_info，自运营写 price_info_list。
  // 类型未知时两者都不写，最终只会报一句「缺少供货价币种」——这里提前说清原因并拦下。
  const capsForPrice = sheinShopCapabilities(form?.shopType || shopType || '');
  if (!capsForPrice.mode) {
    const unknownShopTypeMessage = shopId
      ? `未识别店铺类型（shopType 为空），无法按发品规范组装供货价，请到店铺管理确认该店铺类型后重新刊登（店铺 #${shopId}）`
      : '未识别店铺类型（shopType 为空），无法按发品规范组装供货价，请到店铺管理确认店铺类型后重试';
    const earlyFail = failPrepare('preflight', unknownShopTypeMessage, { title });
    if (earlyFail) return earlyFail;
  }

  const preflight = await runSheinPublishPreflight({
    shopId,
    form,
    isEdit: false,
    attrTpl,
    shopType: form?.shopType || shopType || '',
    siteList,
    standardCache: caches.fillInStandard,
  });
  if (!preflight.ok) {
    const earlyFail = failPrepare(
      'preflight',
      preflight.message || '发布前校验未通过',
      { title, field: preflight.field, section: preflight.section },
    );
    if (earlyFail) return earlyFail;
  }

  const skuPkgCaps = resolveSkuPackageCapabilities(preflight.standardRaw);
  const resolvedSiteList = preflight.siteList || siteList;
  const resolved = resolveShopPublishCurrencies({
    shopType: form?.shopType || shopType || '',
    siteList: resolvedSiteList,
    fillInStandard: preflight.standardRaw,
    preferred: form.currency,
    selectedSites: form.selectedSites,
  });
  const alignedCurrency = resolved.code;
  if (!alignedCurrency) {
    const currencyMessage = resolved.emptyWarning
      || (resolved.source === 'site-list'
        ? '缺少店铺站点币种，请到店铺同步站点后再试'
        : '缺少商品发布规范币种，请到店铺同步后再试');
    const earlyFail = failPrepare('preflight', currencyMessage, { title });
    if (earlyFail) return earlyFail;
  } else {
    form = { ...form, currency: alignedCurrency, siteList: resolvedSiteList };

    // 店铺默认语种标题必填：规范 default_language（常见 zh-cn）；模板默认 en 会导致平台报「商品标题不能为空」
    const defaultLang = extractDefaultLanguage(preflight.standardRaw);
    if (defaultLang) {
      form = {
        ...form,
        titleLanguage: defaultLang,
        shopDefaultLanguage: defaultLang,
      };
    }
  }

  const local = validateSheinPublishForm(form, {
    isEdit: false,
    shopId,
    shopType: form?.shopType || shopType || '',
    attrTpl,
    colorAttr,
    sizeAttr,
    sizeMeasureAttrs,
    warehouseOptions: Array.isArray(warehouses) ? warehouses : [],
    skuPkgCaps,
    linkedAttrRules: null,
  });
  if (!local.ok) {
    const earlyFail = failPrepare(
      'validate',
      local.message || '表单校验未通过',
      { title, field: local.field, section: local.section },
    );
    if (earlyFail) return earlyFail;
  }

  if (collectAllErrors && validationMessages.length) {
    return buildPrepareFailure({
      draftId,
      title,
      stage: 'validate',
      messages: validationMessages,
      meta,
    });
  }

  const submitFormBase = buildSubmitForm(form, { colorAttr, sizeAttr, alignedCurrency });
  const submitForm = {
    ...submitFormBase,
    sizeChart: pruneSizeChartForSubmit(submitFormBase, sizeMeasureAttrs),
  };

  let payload;
  try {
    payload = buildSheinPublishPayload(submitForm, {
      isEdit: false,
      sizeAttrs: sizeMeasureAttrs,
      skuPkgCaps,
      shopType: form?.shopType || shopType || '',
      siteList: resolvedSiteList,
      attrTpl,
      standardRaw: preflight.standardRaw,
    });
  } catch (buildErr) {
    return {
      ok: false,
      draftId,
      title,
      stage: 'build',
      message: buildErr?.message || '无法组装发布参数',
      ...meta,
    };
  }

  const sampleCurrency = samplePublishPayloadCurrency(payload);
  if (!sampleCurrency) {
    return {
      ok: false,
      draftId,
      title,
      stage: 'build',
      message: resolved.source === 'site-list'
        ? '发品参数缺少售价币种（须来自店铺可售站点）'
        : '发品参数缺少供货价币种（须来自商品发布规范）',
      ...meta,
    };
  }

  const skippedNote = skippedListingColorMessage();
  return {
    ok: true,
    draftId,
    title,
    submitForm,
    payload,
    ...(skippedNote ? { warnings: [skippedNote], skippedListingColors } : {}),
    ...meta,
  };
}

/**
 * 单条草稿：拉详情后走与发品页相同的准备链路。
 */
export async function prepareDraftPublish({
  shopId,
  draftId,
  draftMeta = {},
  warehouses = [],
  attrTplCache,
  shopType = '',
  siteList = null,
  prepareCaches = null,
}) {
  const titleHint = draftMeta.title || draftMeta.supplierCode || `草稿#${draftId}`;
  const detail = await sheinPlatformAPI.getPublishDraft(shopId, draftId);
  return prepareFormPublish({
    shopId,
    form: detail?.form || {},
    titleHint,
    draftId,
    warehouses,
    attrTplCache,
    shopType,
    siteList,
    prepareCaches,
  });
}

function draftFormPayload(submitForm) {
  return {
    form: {
      ...submitForm,
      selectedSizeIds: (submitForm.selectedSizeIds?.length
        ? submitForm.selectedSizeIds
        : deriveSelectedSizeIds(submitForm.skcList)
      ).map((id) => String(id)),
    },
  };
}

/**
 * 保存草稿并调用 publish-or-edit（立即返回 job，不等待平台终态；与发品页入队一致）。
 */
export async function enqueuePreparedPublish({
  shopId,
  prepared,
  onHint,
}) {
  const { title, submitForm, payload } = prepared;
  let draftId = prepared.draftId;

  try {
    onHint?.(`正在保存草稿「${title}」…`);
    if (draftId != null && draftId !== '') {
      await sheinPlatformAPI.updatePublishDraft(shopId, draftId, draftFormPayload(submitForm));
    } else {
      const draftRes = await sheinPlatformAPI.createPublishDraft(shopId, draftFormPayload(submitForm));
      if (draftRes?.id != null) draftId = String(draftRes.id);
    }
  } catch {
    // 与发品页一致：保存草稿失败不阻断提交
  }

  onHint?.(`正在提交「${title}」…`);
  const params = draftId != null && draftId !== ''
    ? { draftId: Number(draftId) || draftId }
    : {};
  const job = await sheinPlatformAPI.publishOrEditProduct(shopId, payload, params);
  return {
    ...prepared,
    draftId,
    job,
    jobId: job?.jobId || null,
  };
}

function finalizePublishJobResult(prepared, job) {
  const { draftId, title } = prepared;
  const jobId = job?.jobId || prepared.jobId || null;
  let platformRes = job?.response;
  if (!platformRes && job && (job.code != null || job.info != null)) {
    platformRes = job;
  }
  if (!platformRes && job?.status === 'failed') {
    return {
      ok: false,
      draftId,
      title,
      stage: 'publish',
      message: job?.error || job?.message || '提交失败',
      jobId,
      listingItemId: prepared.listingItemId,
    };
  }

  const res = platformRes || job?.response || job;
  if (!isSheinPublishSuccess(res)) {
    return {
      ok: false,
      draftId,
      title,
      stage: 'publish',
      message: extractSheinPublishFailMessage(res) || job?.error || job?.message || '发布未成功',
      jobId,
      listingItemId: prepared.listingItemId,
    };
  }

  return {
    ok: true,
    draftId,
    title,
    stage: 'publish',
    jobId,
    publishedSpu: job?.publishedSpu || null,
    message: '已提交发布审核',
    listingItemId: prepared.listingItemId,
  };
}

/**
 * 提交并轮询至任务结束（草稿箱批量仍用此路径，同店串行）。
 */
export async function submitPreparedDraftPublish({
  shopId,
  prepared,
  onHint,
}) {
  let enqueued;
  try {
    enqueued = await enqueuePreparedPublish({ shopId, prepared, onHint });
  } catch (ex) {
    return {
      ok: false,
      draftId: prepared.draftId,
      title: prepared.title,
      stage: 'publish',
      message: ex?.message || '提交失败',
      listingItemId: prepared.listingItemId,
    };
  }

  let job = enqueued.job;
  const jobId = job?.jobId;
  if (jobId && !job?.done) {
    job = await waitGoodsSubmitJob(shopId, jobId, onHint);
  }
  return finalizePublishJobResult(enqueued, job);
}

/**
 * 仅轮询已入队任务，得到与 submitPreparedDraftPublish 相同结构的结果。
 */
export async function pollEnqueuedPublishResult({
  shopId,
  enqueued,
  onHint,
}) {
  let job = enqueued?.job;
  const jobId = job?.jobId || enqueued?.jobId;
  if (jobId && (!job || !job.done)) {
    job = await waitGoodsSubmitJob(shopId, jobId, onHint);
  }
  return finalizePublishJobResult(enqueued, job || enqueued?.job);
}

/**
 * @param {object} opts
 * @param {string|number} opts.shopId
 * @param {Array<{ id: string|number, title?: string, supplierCode?: string }>} opts.drafts
 * @param {number} [opts.concurrency=40]
 * @param {(progress: { phase: string, processed: number, total: number, message: string, last?: object }) => void} [opts.onProgress]
 * @returns {Promise<{ success: object[], failed: object[], total: number }>}
 */
export async function batchPublishDrafts({
  shopId,
  drafts,
  shopType = '',
  concurrency = SHEIN_BATCH_PUBLISH_CONCURRENCY,
  onProgress,
}) {
  const list = (Array.isArray(drafts) ? drafts : []).filter((d) => d?.id != null);
  const total = list.length;
  if (!shopId || !total) {
    return { success: [], failed: [], total: 0 };
  }

  let warehouses = [];
  try {
    const wh = await sheinPlatformAPI.getShopWarehouses(shopId);
    warehouses = Array.isArray(wh) ? wh : (Array.isArray(wh?.items) ? wh.items : []);
  } catch {
    warehouses = [];
  }

  let siteList = null;
  try {
    const siteRes = await sheinPlatformAPI.getShopSiteList(shopId);
    siteList = siteRes?.siteList ?? null;
  } catch {
    siteList = null;
  }

  const attrTplCache = new Map();
  const prepareCaches = emptyPrepareCaches();
  const prepConcurrency = Math.max(1, Math.min(Number(concurrency) || SHEIN_BATCH_PUBLISH_CONCURRENCY, SHEIN_BATCH_PUBLISH_CONCURRENCY));

  onProgress?.({
    phase: 'preflight',
    processed: 0,
    total,
    message: `正在校验 0/${total}…（规则 / 属性 / SKU）`,
  });

  const prepared = await mapWithConcurrency(
    list,
    prepConcurrency,
    async (draft) => {
      try {
        return await prepareDraftPublish({
          shopId,
          draftId: draft.id,
          draftMeta: draft,
          warehouses,
          attrTplCache,
          shopType,
          siteList,
          prepareCaches,
        });
      } catch (ex) {
        return {
          ok: false,
          draftId: draft.id,
          title: draft.title || draft.supplierCode || `草稿#${draft.id}`,
          stage: 'prepare',
          message: ex?.message || '准备发布失败',
        };
      }
    },
    (done, all, result) => {
      onProgress?.({
        phase: 'preflight',
        processed: done,
        total: all,
        message: result?.ok
          ? `校验通过 ${done}/${all}：${result.title || ''}`
          : `校验失败 ${done}/${all}：${result?.message || ''}`,
        last: result,
      });
    },
  );

  const ready = prepared.filter((row) => row?.ok);
  const failed = prepared.filter((row) => row && !row.ok);
  const success = [];

  onProgress?.({
    phase: 'publish',
    processed: failed.length,
    total,
    message: ready.length
      ? `校验完成，开始发布 0/${ready.length}…`
      : '全部未通过校验',
  });

  // 同店发品任务 FIFO：提交串行，避免无 MQ 时多请求互锁超时
  for (let i = 0; i < ready.length; i += 1) {
    const item = ready[i];
    onProgress?.({
      phase: 'publish',
      processed: failed.length + success.length,
      total,
      message: `正在发布 ${i + 1}/${ready.length}：${item.title || item.draftId}`,
    });
    // eslint-disable-next-line no-await-in-loop
    const submitted = await submitPreparedDraftPublish({
      shopId,
      prepared: item,
      onHint: (hint) => {
        onProgress?.({
          phase: 'publish',
          processed: failed.length + success.length,
          total,
          message: hint || `正在发布 ${i + 1}/${ready.length}`,
        });
      },
    });
    if (submitted.ok) success.push(submitted);
    else failed.push(submitted);

    onProgress?.({
      phase: 'publish',
      processed: failed.length + success.length,
      total,
      message: submitted.ok
        ? `已发布 ${success.length}/${ready.length}：${submitted.title || ''}`
        : `发布失败：${submitted.message || ''}`,
      last: submitted,
    });
  }

  return { success, failed, total };
}
